import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Tooltip, TextField, InputAdornment, Collapse } from '@mui/material';
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Palette de couleurs prédéfinies
const presetColors = [
  // Couleurs primaires
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  // Couleurs secondaires
  '#ff5722',
  '#795548',
  '#607d8b',
  '#9e9e9e',
  '#000000',
  // Tons pastels
  '#ffcdd2',
  '#f8bbd0',
  '#e1bee7',
  '#d1c4e9',
  '#c5cae9',
  '#bbdefb',
  '#b3e5fc',
  '#b2ebf2',
  '#b2dfdb',
  '#c8e6c9',
  // Tons foncés
  '#b71c1c',
  '#880e4f',
  '#4a148c',
  '#311b92',
  '#1a237e',
  '#0d47a1',
  '#01579b',
  '#006064',
  '#004d40',
  '#1b5e20'
];

/**
 * Composant ColorPicker inline
 *
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.value - La valeur de la couleur actuelle (format hex)
 * @param {Function} props.onChange - Fonction appelée quand la couleur change
 * @param {string} props.label - Libellé du champ (optionnel)
 * @returns {JSX.Element} Composant ColorPicker
 */
function ColorPicker({ value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const [hexValue, setHexValue] = useState(value || '');
  const { formatMessage: msg } = useIntl();

  // Gestion de la saisie manuelle de couleur hexadécimale
  const handleHexChange = (e) => {
    const newValue = e.target.value;
    setHexValue(newValue);

    // Validation du format hex (avec ou sans #)
    const hexRegex = /^#?([0-9A-F]{3}){1,2}$/i;
    if (hexRegex.test(newValue)) {
      // Si l'utilisateur a omis le #, on l'ajoute
      const formattedValue = newValue.startsWith('#') ? newValue : `#${newValue}`;
      onChange(formattedValue);
    }
  };

  // Sélection d'une couleur prédéfinie
  const handleColorSelect = (color) => {
    setHexValue(color);
    onChange(color);
    setOpen(false);
  };

  return (
    <MDBox sx={{ mb: 2 }}>
      <MDBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1
        }}
      >
        <MDTypography variant="subtitle2">
          {label || msg({ id: 'field.color.label' }) || 'Couleur'}
        </MDTypography>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: value || '#ffffff',
            border: '1px solid #e0e0e0',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
          onClick={() => setOpen(!open)}
        />
      </MDBox>

      <Collapse in={open}>
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1,
            backgroundColor: 'background.paper',
            boxShadow: 3
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="#RRGGBB"
            value={hexValue}
            onChange={handleHexChange}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '4px',
                      backgroundColor: hexValue || '#ffffff',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </InputAdornment>
              )
            }}
          />

          <Grid container spacing={1} sx={{ maxHeight: '200px', overflow: 'auto' }}>
            {presetColors.map((color) => (
              <Grid item key={color} xs={2} sm={1}>
                <Tooltip title={color} arrow>
                  <Box
                    sx={{
                      width: 25,
                      height: 25,
                      borderRadius: '4px',
                      backgroundColor: color,
                      cursor: 'pointer',
                      border: value === color ? '2px solid black' : '1px solid #e0e0e0',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => handleColorSelect(color)}
                  />
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </MDBox>
  );
}

ColorPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string
};

export default ColorPicker;
